import React, { createContext, useContext, useEffect, useReducer, useCallback } from 'react';
import { useFirebaseAuth } from './FirebaseAuthProvider';
import NotificationService from '../services/NotificationService';


export const SubscriptionContext = createContext();

const subscriptionReducer = (state, action) => {
  switch (action.type) {
    case 'SET_SUBSCRIPTION':
      return { ...state, ...action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    default:
      return state;
  }
};

export const SubscriptionProvider = ({ children }) => {
  const { user, getToken } = useFirebaseAuth();
  const [state, dispatch] = useReducer(subscriptionReducer, {
    tier: 'free',
    status: 'active',
    currentPeriodEnd: null,
    trialEnd: null,
    isLoading: false,
    error: null
  });

  const fetchSubscription = useCallback(async () => {
    if (!user) return;
    
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const token = await getToken();
      const response = await fetch('/api/subscription/current', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch subscription');
      
      const data = await response.json();
      dispatch({ type: 'SET_SUBSCRIPTION', payload: data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [user, getToken]);

  useEffect(() => {
    if (user) {
      fetchSubscription();
    }
  }, [user]);

  const startSubscription = async (tier, paymentMethodId) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const token = await getToken();
      const response = await fetch('/api/subscription/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ tier, paymentMethodId })
      });

      if (!response.ok) throw new Error('Subscription creation failed');
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Subscription error:', error);
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const updateSubscriptionStatus = async (subscription) => {
    if (!user) return;
    
    dispatch({ 
      type: 'SET_SUBSCRIPTION', 
      payload: {
        tier: subscription.tier,
        status: subscription.status,
        currentPeriodEnd: subscription.currentPeriodEnd,
        trialEnd: subscription.trialEnd,
        cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
        pauseCollection: subscription.pauseCollection,
        credits: subscription.credits
      }
    });

    if (subscription.trialEnd) {
      const trialEndDate = new Date(subscription.trialEnd);
      const now = new Date();
      const daysUntilTrialEnd = Math.ceil((trialEndDate - now) / (1000 * 60 * 60 * 24));
      
      if (daysUntilTrialEnd <= 3) {
        await NotificationService.sendSubscriptionStatusEmail(
          user.email, 
          {
            type: 'trial_ending',
            tier: subscription.tier,
            endDate: trialEndDate
          }
        );
      }
    }
  };

  return (
    <SubscriptionContext.Provider value={{ 
      ...state, 
      updateSubscriptionStatus,
      fetchSubscription
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);
